import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAgo } from '@/lib/ago';
import { humanizeAction } from '@/lib/format';
import type { AuditLog } from '@/types/auditLog';

export interface ActivityPanelProps {
  /** Newest first, as returned by the API. */
  logs: AuditLog[];
}

const LIMIT = 8;

/** Recent audit activity with a link to the full log. Wrap in a Panel on the page. */
export function ActivityPanel({ logs }: ActivityPanelProps) {
  const rows = logs.slice(0, LIMIT);

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((log) => {
            const action = humanizeAction(log.action);
            const meta = [`by ${log.api_key_name || log.actor_type}`, log.resource_type].filter(Boolean).join(' · ');
            return (
              <li key={log.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div title={action} className="truncate font-medium">{action}</div>
                  <div title={meta} className="truncate text-xs text-muted-foreground">{meta}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!log.success && <StatusBadge status="failed" label="failed" />}
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatAgo(Date.parse(log.created_at))}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Link
        to="/audit-logs"
        className="inline-block rounded-sm text-sm font-medium outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
      >
        View all audit logs
      </Link>
    </div>
  );
}
